import PropTypes from "prop-types";
import { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { Bot, Activity, X } from "lucide-react";
import { Box, IconButton, Typography } from "@mui/material";
import "./FloatingCopilotButton.css";
import CopilotSidebar from "./Copilot.jsx";
import copilotService from "../services/copilotService.js";
import { useTheme } from "../theme/useTheme.js";
import { useAuth } from "./contexts/AuthContext.jsx";

// Los tutoriales comparten este marcador para que componentes globales,
// como el popup proactivo de Copilot, sepan que deben inhibirse.
const TUTORIAL_ACTIVE_SELECTOR = '[data-tutorial-active="true"]';

const getTodayKey = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `copilot_summary_shown_${yyyy}-${mm}-${dd}`;
};

const SUMMARY_PROMPT = "Genera mi resumen de actividad: muéstrame los mensajes y notificaciones no leídos.";

const FloatingCopilotButton = ({ variant = "floating" }) => {
  const [openCopilot, setOpenCopilot] = useState(false);
  const [copilotPrompt, setCopilotPrompt] = useState("");
  const [copilotLaunchToken, setCopilotLaunchToken] = useState(0);
  const [preloadedThread, setPreloadedThread] = useState(null);
  const [preloadedMessage, setPreloadedMessage] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [showDailyPopup, setShowDailyPopup] = useState(false);
  const [popupPos, setPopupPos] = useState(null);
  const [tutorialActive, setTutorialActive] = useState(false);
  const buttonRef = useRef(null);
  const { user } = useAuth();
  const isClient = user?.role === "client";
  const isTicketFreelance = user?.role === "freelance_ticket";
  const canShowDailySummaryPopup =
    Boolean(user) &&
    !isClient &&
    !isTicketFreelance &&
    !tutorialActive;

  const { getRoleBranding } = useTheme();
  const branding = getRoleBranding(user?.role);
  const mainColor = branding.primary || "#E6007E";
  const lightColor = branding.light || "#F2596A";
  const darkColor = branding.dark || "#C40069";
  const contrastText = branding.contrastText || "#ffffff";
  const shadowColor = branding.shadow || "rgba(230, 0, 126, 0.25)";
  const rippleColor = branding.ripple || "rgba(230, 0, 126, 0.2)";
  const gradient = branding.gradient || mainColor;

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--copilot-main", mainColor);
    root.style.setProperty("--copilot-light", lightColor);
    root.style.setProperty("--copilot-dark", darkColor);
    root.style.setProperty("--copilot-contrast", contrastText);
    root.style.setProperty("--copilot-shadow", shadowColor);
    root.style.setProperty("--copilot-ripple", rippleColor);
    root.style.setProperty("--copilot-gradient", gradient);
  }, [mainColor, lightColor, darkColor, contrastText, shadowColor, rippleColor, gradient]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    // Observa el DOM para detectar tanto la apertura del modal intro
    // como los pasos del joyride, sin depender del estado interno de cada página.
    const syncTutorialState = () => {
      setTutorialActive(Boolean(document.querySelector(TUTORIAL_ACTIVE_SELECTOR)));
    };

    syncTutorialState();

    const observer = new MutationObserver(syncTutorialState);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-tutorial-active"],
    });

    return () => observer.disconnect();
  }, []);

  // Popup proactivo: primera entrada del día
  useEffect(() => {
    if (!canShowDailySummaryPopup) return;
    // Si ya se mostró hoy, no volver a mostrarlo
    if (localStorage.getItem(getTodayKey())) return;
    const timer = setTimeout(() => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setPopupPos({ top: rect.bottom + 10, right: Math.max(12, window.innerWidth - rect.right) });
      }
      setShowDailyPopup(true);
    }, 500);
    return () => clearTimeout(timer);
  }, [canShowDailySummaryPopup, user?.id, user?.role]);

  useEffect(() => {
    // Si el tutorial arranca con el popup ya visible, lo cerramos para no solapar UI.
    if (!tutorialActive) return;
    setShowDailyPopup(false);
  }, [tutorialActive]);

  const dismissDailyPopup = () => {
    localStorage.setItem(getTodayKey(), "1");
    setShowDailyPopup(false);
  };

  const handleOpenFromPopup = async () => {
    dismissDailyPopup();
    setLoadingSummary(true);
    try {
      // Crear thread y enviar mensaje ANTES de abrir el sidebar
      const thread = await copilotService.createThread(null);
      const message = await copilotService.sendMessage(thread.id, SUMMARY_PROMPT);
      setPreloadedThread(thread);
      setPreloadedMessage(message);
      setCopilotPrompt(SUMMARY_PROMPT);
      setCopilotLaunchToken((prev) => prev + 1);
      setOpenCopilot(true);
    } catch (err) {
      console.error("Error creating summary thread:", err);
    } finally {
      setLoadingSummary(false);
    }
  };

  const handleOpenNormal = () => {
    setCopilotPrompt("");
    setOpenCopilot(true);
  };

  // No renderizar el Copilot para usuarios cliente
  if (isClient) {
    return null;
  }

  const popupStyle = popupPos
    ? { "--copilot-main": mainColor, top: popupPos.top, right: popupPos.right, bottom: "auto", left: "auto" }
    : { "--copilot-main": mainColor };

  const dailyPopup = showDailyPopup && ReactDOM.createPortal(
    <div className="copilot-daily-popup" style={popupStyle}>
      <button
        className="copilot-daily-popup__close"
        onClick={dismissDailyPopup}
        aria-label="Cerrar"
      >
        <X size={14} />
      </button>
      <div className="copilot-daily-popup__icon">
        <Activity size={18} color={mainColor} />
      </div>
      <Typography
        variant="body2"
        fontWeight={600}
        color="#3a2f64"
        sx={{ mb: 0.5 }}
      >
        Resumen de actividad
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
        ¿Quieres ver los últimos mensajes y eventos no leídos?
      </Typography>
      <button
        className="copilot-daily-popup__cta"
        style={{ backgroundColor: mainColor, color: contrastText, opacity: loadingSummary ? 0.7 : 1 }}
        onClick={handleOpenFromPopup}
        disabled={loadingSummary}
      >
        {loadingSummary ? "Cargando..." : "Ver resumen de actividad"}
      </button>
    </div>,
    document.body
  );

  if (variant === "header") {
    return (
      <>
        <Box ref={buttonRef} data-welcome-tour="copilot" sx={{ marginLeft: "8px", display: "flex", alignItems: "center" }}>
          <IconButton
            onClick={handleOpenNormal}
            sx={{
              width: "40px",
              height: "40px",
              borderRadius: "20px",
              background: gradient,
              color: contrastText,
              "&:hover": {
                background: gradient,
                filter: "brightness(0.95)",
                transform: "scale(1.05)",
                animation: "none",
              },
              transition: "all 0.2s ease",
              boxShadow: `0 2px 8px ${shadowColor}`,
              animation: "subtlePulse 2.5s ease-in-out infinite",
            }}
            aria-label="Abrir Copilot"
          >
            <Bot size={20} />
          </IconButton>
        </Box>

        {dailyPopup}

        {openCopilot && (
          <CopilotSidebar
            key={copilotLaunchToken}
            onClose={() => { setOpenCopilot(false); setPreloadedThread(null); setPreloadedMessage(null); }}
            initialPrompt={copilotPrompt}
            autoSendOnOpen={Boolean(copilotPrompt) && !preloadedThread}
            preloadedThread={preloadedThread}
            preloadedMessage={preloadedMessage}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className="floating-copilot-container">
        <button
          className="floating-copilot-button"
          onClick={handleOpenNormal}
        >
          <Bot size={24} />
        </button>
      </div>

      {dailyPopup}

      {openCopilot && (
        <CopilotSidebar
          key={copilotLaunchToken}
          onClose={() => { setOpenCopilot(false); setPreloadedThread(null); setPreloadedMessage(null); }}
          initialPrompt={copilotPrompt}
          autoSendOnOpen={Boolean(copilotPrompt) && !preloadedThread}
          preloadedThread={preloadedThread}
          preloadedMessage={preloadedMessage}
        />
      )}
    </>
  );
};

FloatingCopilotButton.propTypes = {
  variant: PropTypes.oneOf(["floating", "header"]),
};

export default FloatingCopilotButton;
